import React, { useMemo, useState } from 'react';
import { DragDropContext } from '@hello-pangea/dnd';
import { Link, useNavigate } from 'react-router';

// Design System Components
import Alert from 'Components/DesignSystem/Alert/Alert';
import ErrorState from 'Components/DesignSystem/ErrorState/ErrorState';

// Module Components
import EditorBody from './EditorBody';
import EditorSkeleton from './EditorSkeleton';
import EditorHeader from './EditorHeader';
import SaveIndicator from './SaveIndicator';
import { CardPreview } from './preview';
import { ConfigurationPane } from './rail';
// The properties column is the editor body's own third child, not pane content
// (spec R59, R65) — so it is imported past the pane's barrel, which no longer
// exports it. Its file is still in `rail/` pending the rename ARCHITECTURE §2 asks
// for; see IMPLEMENTATION.md.
import CardPropertiesPanel from './rail/CardPropertiesPanel';
import PropertiesPanel from './rail/PropertiesPanel';

// Contexts and Hooks
import { useCardEditor } from '../../contexts';
import { useBlockDnd, useEditorChrome, useInspector } from '../../hooks';

// Selectors and State
import { selectPropertyRegionName, selectVisibleFields } from '../../selectors';
import { cardActions, getFieldConfiguration } from '../../state';

// Constants
import {
  IMAGE_FIELD_ID,
  PRODUCT_CARDS_ROUTE,
  STRINGS,
  getFieldDefinition,
} from '../../constants';

// Types
import {
  CardOrientation,
  CtaConfiguration,
  DescriptionStyle,
  FieldAlignment,
  FieldId,
  ImageFit,
} from '../../types';

/**
 * The Card Editor: header, then the editor body's **column contract** — the
 * configuration pane on the left, the live preview beside it, and the properties
 * column last while an element is selected (spec R59, R63, R65). All of it wired to
 * one reducer, with no Save control anywhere (spec R8, R45).
 *
 * Two columns, three while inspecting. The pane is the **first child**, not the
 * second one painted on the left: DOM order is what a screen reader reads and what
 * Tab follows, and R59 asks for the pane to lead in both (ADR-2C). The properties
 * column is the **last** child of the same row rather than a `position: fixed`
 * overlay, so its 320px is reserved by the layout itself and no part of the preview
 * can end up underneath it (ADR-3A, R65).
 *
 * The row itself — its three slots, the collapse controls, and which side column is
 * currently zero-width — belongs to `EditorBody` (spec R75–R77). This component
 * keeps the card wiring and hands that shell three slots, so collapsing a panel
 * cannot reach any card state: it is chrome, and chrome is not card data (ADR-5A).
 *
 * The field search, the inspector target and the two collapse booleans are all view
 * state and stay here; nothing about them belongs in the persisted card (ADR-3B,
 * ADR-5A).
 *
 * From `md:` up — where the three columns sit side by side — this component is also
 * the editor's **height** boundary (spec R74): a bounded column whose header is fixed
 * and whose body row takes the rest, so each column scrolls its own overflow instead
 * of the whole editor scrolling inside the app shell. Below `md:` the columns stack
 * and the page scrolls them, as it always has.
 */

/**
 * The editor's height while it is a row of columns (`md:` and up).
 *
 * `Components/Templates/Layout` gives every route a `calc(100vh-60px)` `main` with
 * `overflow-auto` and `pt-3`, and wraps the page in a `min-h-full tw-flex tw-flex-col`
 * column (`Layout.tsx:19`) that declares **no height of its own**. A `min-height` is a
 * floor, not a basis: a percentage height inside a wrapper whose own height is `auto`
 * has nothing definite to resolve against and falls back to `auto`. So an editor that
 * asks for its height (`h-full`) instead of stating it is sized by its own content,
 * grows past `main`'s box, and `main` scrolls all three columns together — exactly the
 * fault R74 sends back.
 *
 * **So the height is stated outright, not inherited** (spec R74, second send-back).
 * `calc(100vh-72px)` is the box `main` actually offers: the viewport less the 60px
 * application header and less `main`'s own 12px of top padding. Asking for `h-full`
 * and capping it with `max-h-[calc(100vh-72px)]` was the same intent one indirection
 * too far — with the wrapper indefinite the request resolved to `auto`, the box grew
 * to its 1095px subtree inside an 809px viewport, and the cap sat in the DOM without
 * bounding the rendered box. Every descendant then resolved its own `h-full` against
 * that oversized box: `EditorBody`'s row stretched to it, the preview's
 * `overflow-y-auto` was handed a box already tall enough to hold 100% of its content,
 * and a wheel over the preview found nothing to scroll — the Footer region stayed
 * off-screen and unreachable.
 *
 * A definite height is what the whole chain below needs: the header keeps its own
 * height, `EditorBody`'s row (`md:tw-flex-1 md:tw-min-h-0`) takes a real remainder,
 * and the preview wrapper's `md:tw-h-full` finally resolves to a box **shorter than
 * its content** — a genuine scroll container, with nothing left over for `main` to
 * scroll (spec R74).
 *
 * `min-h-0` and `overflow-hidden` keep that height honest. This element is a flex item
 * of `Layout`'s column, and a flex item's `min-height: auto` resolves to its content's
 * min-content height unless its own `overflow` is something other than `visible`; the
 * used height is `max(min-height, min(height, max-height))`, so a 1095px-tall subtree
 * would otherwise raise this box straight back past the height it just declared.
 * `overflow-hidden` states the same guarantee structurally — it removes that automatic
 * minimum on its own (belt to `min-h-0`'s braces) and keeps any residual overflow
 * inside the editor's box instead of pushing into `main`.
 *
 * Both moves — a literal height on the shell, `overflow-hidden` on the box that must
 * stop growing — are what the Guide builder's own shell already does
 * (`ISR/UI-Model-1/.../EditorLayout.tsx:83` and `:96`), so the two builders converge
 * rather than growing a third height-boundary technique. Deliberately not its literal
 * `h-screen`: this route is already inside `main`, so a full viewport height would
 * overshoot that chrome by exactly the 72px subtracted here.
 *
 * All three classes stay `md:`-scoped. Below `md:` the columns stack and the *page*
 * scrolls them by design — clipping an unbounded stack there would cut the last
 * column off.
 */
const EDITOR_HEIGHT_CLASS =
  'md:tw-h-[calc(100vh-72px)] md:tw-min-h-0 md:tw-overflow-clip';

/**
 * The editor's outer box, shared by the loading and loaded renders (2026-09-16).
 * One constant rather than two equal strings: the whole point of the skeleton is
 * that the header does not move when the card lands, and two literals drift.
 */
const EDITOR_SHELL_CLASS = `tw-flex tw-flex-col tw-max-w-full ${EDITOR_HEIGHT_CLASS}`;

const CardEditor = () => {
  const {
    card,
    isLoading,
    notFound,
    saveError,
    saveErrorField,
    status,
    lastSavedAt,
    dispatch,
    flush,
    retry,
  } =
    useCardEditor();
  const navigate = useNavigate();

  const [fieldSearch, setFieldSearch] = useState('');
  const inspector = useInspector();
  const chrome = useEditorChrome();

  const visibleFields = useMemo(
    () => (card ? selectVisibleFields(card, fieldSearch) : []),
    [card, fieldSearch]
  );

  const visibleFieldIds = useMemo(
    () => visibleFields.map((field) => field.id),
    [visibleFields]
  );

  const dnd = useBlockDnd(card, dispatch, visibleFieldIds);

  // The one inspector target (ADR-3B): non-null means the editor shows three
  // columns, and it is the element whose properties the third one renders.
  const inspectedFieldId = inspector.target;

  const handleBack = () => {
    // Write the pending edit before the route changes, so the listing and a
    // re-opened editor both show it (spec R30, R35).
    flush();
    navigate(PRODUCT_CARDS_ROUTE);
  };

  const handleToggleField = (fieldId: FieldId, enabled: boolean) => {
    dispatch(cardActions.toggleField(fieldId, enabled));
    if (!enabled) {
      // An unticked field has no properties affordance to come back to.
      inspector.dismiss(fieldId);
    }
  };

  const handleRemoveBlock = (fieldId: FieldId) => {
    // Removing a block also unticks its field (spec R57), from the preview's own
    // delete control (R56) and from the properties panel's remove control (R63).
    dispatch(cardActions.deleteBlock(fieldId));
    inspector.dismiss(fieldId);
  };

  if (isLoading) {
    /* The chrome stays and the geometry holds (2026-09-16). This was a lone
       centred spinner with the header unmounted, so the user had no way back for
       the length of the fetch and the whole editor arrived in one jump. Same
       shell, same header box, the three columns at their real widths. `handleBack`
       is safe here: `flush()` writes only a PENDING edit, and a card that has not
       loaded has none. */
    return (
      <div className={EDITOR_SHELL_CLASS} data-testid="card-editor-shell">
        <EditorHeader
          loading
          name=""
          onBack={handleBack}
          onRename={() => undefined}
        />
        <EditorSkeleton />
      </div>
    );
  }

  if (notFound || !card) {
    return (
      <ErrorState
        size="sm"
        title={STRINGS.editor.notFound}
        className="tw-py-6"
        data-testid="card-editor-not-found"
        action={
          <Link to={PRODUCT_CARDS_ROUTE} className="tw-text-accent tw-text-title tw-underline">
            {STRINGS.editor.backToListing}
          </Link>
        }
      />
    );
  }

  return (
    <div className={EDITOR_SHELL_CLASS} data-testid="card-editor-shell">
      <EditorHeader
        name={card.name}
        onBack={handleBack}
        onRename={(name) => dispatch(cardActions.rename(name))}
        saveIndicator={
          <SaveIndicator
            status={status}
            lastSavedAt={lastSavedAt}
            onRetry={retry}
            isInputRefusal={saveErrorField !== null}
          />
        }
      />

      {saveError && (
        // `role="alert"` — this is the one message in the editor the user has to
        // act on, and without it a screen-reader user gets no announcement at
        // all: the banner simply appears in the page. The visible indicator in
        // the header carries the same failure politely, so the two together
        // cover both "I am watching" and "I am not".
        <Alert tone="danger" live className="tw-mx-4 tw-my-2" data-testid="card-editor-save-error">
          {saveError}
        </Alert>
      )}

      <DragDropContext
        onDragStart={dnd.onDragStart}
        onDragUpdate={dnd.onDragUpdate}
        onDragEnd={dnd.onDragEnd}
      >
        <EditorBody
          chrome={chrome}
          pane={
            <ConfigurationPane
              card={card}
              visibleFields={visibleFields}
              searchTerm={fieldSearch}
              inspector={inspector}
              onSearchChange={setFieldSearch}
              onToggleField={handleToggleField}
            />
          }
          preview={
            <CardPreview
              card={card}
              selectedFieldId={inspectedFieldId}
              activeRegion={dnd.activeRegion}
              isDragging={dnd.isDragging}
              onSelectBlock={inspector.openFromBlock}
              onDeleteBlock={handleRemoveBlock}
              onClearSelection={inspector.close}
            />
          }
          panel={
            // The inspector is always mounted; only its SUBJECT changes. With
            // nothing selected it shows the card's own properties, which is the
            // operator's §2.4 contract and what moved orientation out of the pane
            // on 2026-08-15. A column that appears and disappears would also make
            // the preview jump 320px every time a block is selected.
            inspectedFieldId === null ? (
              <CardPropertiesPanel
                orientation={card.orientation}
                footerEnabled={card.footerEnabled}
                onOrientationChange={(orientation: CardOrientation) =>
                  dispatch(cardActions.setOrientation(orientation))
                }
                onFooterChange={(enabled) => dispatch(cardActions.setFooterEnabled(enabled))}
              />
            ) : (
              <PropertiesPanel
                definition={getFieldDefinition(inspectedFieldId)}
                configuration={getFieldConfiguration(card, inspectedFieldId)}
                regionName={selectPropertyRegionName(card, inspectedFieldId)}
                // The Primary Image is the photo background rather than a placed
                // block, so there is nothing to remove from the card (spec R63).
                onRemove={
                  inspectedFieldId === IMAGE_FIELD_ID
                    ? null
                    : () => handleRemoveBlock(inspectedFieldId)
                }
                onBack={inspector.back}
                onDisplayLabelChange={(displayLabel) =>
                  dispatch(cardActions.setDisplayLabel(inspectedFieldId, displayLabel))
                }
                onAlignmentChange={(alignment: FieldAlignment) =>
                  dispatch(cardActions.setAlignment(inspectedFieldId, alignment))
                }
                onImageFitChange={(imageFit: ImageFit) =>
                  dispatch(cardActions.setImageFit(imageFit))
                }
                onDescriptionStyleChange={(descStyle: DescriptionStyle) =>
                  dispatch(cardActions.setDescriptionStyle(descStyle))
                }
                // Per element since 14021, so it carries the inspected field id —
                // as the strikethrough toggle below it always did.
                onShowUomChange={(showUom) =>
                  dispatch(cardActions.setShowUom(inspectedFieldId, showUom))
                }
                // Per element, so it DOES carry it — the difference between this and
                // the line above is the whole reason they are separate sections (14021).
                onStrikethroughChange={(strikethrough) =>
                  dispatch(cardActions.setStrikethrough(inspectedFieldId, strikethrough))
                }
                onCtaOptionsChange={(options: Partial<CtaConfiguration>) =>
                  dispatch(cardActions.setCtaOptions(options))
                }
                onCustomCssChange={(customCss) =>
                  dispatch(cardActions.setCustomCss(inspectedFieldId, customCss))
                }
              />
            )
          }
        />
      </DragDropContext>
    </div>
  );
};

export default CardEditor;
